import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { authWrite } from '../lib/authWrite';
import { useToast } from '../lib/Toast';
import { humanizeError } from '../lib/humanizeError';
import { computeDueDate, gstSplit, fmtDate, fmtMoney, TERMS, CURRENCIES, FULFILMENT, INVOICE_STATUSES } from './b2bLib';

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function NewInvoiceModal({ prefill, customers, session, onClose, onCreated }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(() => ({
    customer_id: prefill?.customer_id || '',
    invoice_ref: '', po_ref: '', sales_order_ref: '', skutopia_url: '',
    issue_date: todayISO(), terms: '30 Days', currency: 'AUD',
    amount_inc_gst: '', status: 'Draft', fulfilment_status: 'Not Sent', eta: '',
  }));

  const cust = useMemo(() => customers.find(c => c.customer_id === f.customer_id) || null, [customers, f.customer_id]);

  const pickCustomer = (id) => {
    const c = customers.find(x => x.customer_id === id);
    setF(s => ({ ...s, customer_id: id, currency: c?.currency || s.currency, terms: c?.default_terms || s.terms }));
  };

  const inc = f.amount_inc_gst === '' ? null : Number(f.amount_inc_gst);
  const due = f.issue_date && f.terms ? computeDueDate(f.issue_date, f.terms) : null;
  const split = inc == null ? null : gstSplit(inc, f.currency);

  const save = async () => {
    if (!f.customer_id) { toast.error('Pick a customer', 'A B2B invoice must belong to a customer.'); return; }
    setBusy(true);
    try {
      const rows = await authWrite(session, 'b2b_invoices', 'insert', {
        customer_id: f.customer_id,
        invoice_ref: f.invoice_ref || null, po_ref: f.po_ref || null, sales_order_ref: f.sales_order_ref || null,
        skutopia_url: f.skutopia_url || null, issue_date: f.issue_date || null, due_date: due,
        terms: f.terms || null, currency: f.currency,
        amount_inc_gst: inc, amount_ex_gst: split ? split.ex : null, gst: split ? split.gst : null,
        status: f.status, fulfilment_status: f.fulfilment_status, eta: f.eta || null,
        created_by: session?.code || null,
      });
      const id = Array.isArray(rows) && rows[0] ? rows[0].id : null;
      if (id) {
        await authWrite(session, 'b2b_activity', 'insert', {
          invoice_id: id, occurred_at: new Date().toISOString(), actor: session?.name || session?.code || 'staff',
          type: 'draft', body: `Invoice created (${cust?.name || ''})`,
        });
      }
      toast.success('Invoice created');
      onCreated && onCreated(id);
    } catch (e) {
      const { title, body } = humanizeError(e, 'Could not create invoice'); toast.error(title, body); setBusy(false);
    }
  };

  return createPortal(
    <>
      <div onClick={() => !busy && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 220, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 221, width: 560, maxWidth: '94vw', maxHeight: '88vh', background: '#fff', borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 22px', borderBottom: '1px solid #e4e4e7', fontSize: 16, fontWeight: 700 }}>New invoice</div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
          <div className="form-group">
            <label className="form-label">Customer</label>
            <select className="form-select" value={f.customer_id} onChange={e => pickCustomer(e.target.value)} autoFocus>
              <option value="">Select customer…</option>
              {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name} ({c.currency})</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FG label="Invoice ref"><input className="form-input" value={f.invoice_ref} onChange={e => setF(s => ({ ...s, invoice_ref: e.target.value }))} /></FG>
            <FG label="PO ref"><input className="form-input" value={f.po_ref} onChange={e => setF(s => ({ ...s, po_ref: e.target.value }))} /></FG>
            <FG label="Issue date"><input type="date" className="form-input" value={f.issue_date} onChange={e => setF(s => ({ ...s, issue_date: e.target.value }))} /></FG>
            <FG label="Terms"><select className="form-select" value={f.terms} onChange={e => setF(s => ({ ...s, terms: e.target.value }))}>{TERMS.map(t => <option key={t}>{t}</option>)}</select></FG>
            <FG label="Currency"><select className="form-select" value={f.currency} onChange={e => setF(s => ({ ...s, currency: e.target.value }))}>{CURRENCIES.map(c => <option key={c}>{c}</option>)}</select></FG>
            <FG label="Amount inc GST"><input type="number" step="0.01" className="form-input" value={f.amount_inc_gst} onChange={e => setF(s => ({ ...s, amount_inc_gst: e.target.value }))} /></FG>
            <FG label="Status"><select className="form-select" value={f.status} onChange={e => setF(s => ({ ...s, status: e.target.value }))}>{INVOICE_STATUSES.filter(s2 => s2 !== 'Cancelled').map(s2 => <option key={s2}>{s2}</option>)}</select></FG>
            <FG label="Fulfilment"><select className="form-select" value={f.fulfilment_status} onChange={e => setF(s => ({ ...s, fulfilment_status: e.target.value }))}>{FULFILMENT.map(x => <option key={x}>{x}</option>)}</select></FG>
            <FG label="Sales order ref"><input className="form-input" value={f.sales_order_ref} onChange={e => setF(s => ({ ...s, sales_order_ref: e.target.value }))} /></FG>
            <FG label="ETA"><input type="date" className="form-input" value={f.eta} onChange={e => setF(s => ({ ...s, eta: e.target.value }))} /></FG>
          </div>
          <FG label="Skutopia URL"><input className="form-input" value={f.skutopia_url} onChange={e => setF(s => ({ ...s, skutopia_url: e.target.value }))} /></FG>
          <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 6 }}>
            Due {due ? fmtDate(due) : '—'}{split ? ` · GST ${fmtMoney(split.gst, f.currency)} · ex ${fmtMoney(split.ex, f.currency)}` : ''}
            {f.currency !== 'AUD' ? ' · export, GST-free' : ''}
          </div>
        </div>
        <div style={{ padding: '12px 22px', borderTop: '1px solid #e4e4e7', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" disabled={busy || !f.customer_id} onClick={save}>Create invoice</button>
        </div>
      </div>
    </>,
    document.body,
  );
}

function FG({ label, children }) { return <div className="form-group"><label className="form-label">{label}</label>{children}</div>; }
